"use client";

import { useEffect, useState } from "react";
import useExecEdDetailPageTest from "@/monarch/rules/execEdDetailPageTest/useExecEdDetailPageTest";
import type { FormData } from "@/components/TaxiForm/types";

const TAXI_FORM_PACKAGE_BASE_URL = "https://prospect-form-packages.2u.com/all";

type ExecEdFormIdOverride = {
  formId: string;
  taxiFormData?: FormData | null;
  /**
   * True until the effective form id is known. The lead form plugin binds to a
   * single package on mount, so callers must not render the form while this is
   * true or the plugin initialises against the pre-override package.
   */
  isResolving: boolean;
};

async function fetchTaxiFormPackage(formId: string): Promise<FormData> {
  // Omit x-client-id on purpose: that custom header triggers a CORS OPTIONS
  // preflight that CloudFront rejects, so the browser never receives the
  // package. Server-side getTaxiFormData still sends the header.
  const encodedFormId = encodeURIComponent(formId);
  const response = await fetch(
    `${TAXI_FORM_PACKAGE_BASE_URL}/${encodedFormId}.json`,
  );

  if (!response.ok) {
    throw new Error(
      `Error fetching taxi form data: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Resolves which lead form an Exec Ed detail page renders. Monarch may supply a
 * form id for the active variant; when it does not, or when its package cannot
 * be loaded, the CMS form is used unchanged.
 */
export default function useExecEdFormIdOverride(
  cmsFormId: string,
  cmsTaxiFormData?: FormData | null,
): ExecEdFormIdOverride {
  const { isLoading, data } = useExecEdDetailPageTest();
  const monarchFormId =
    data?.formId && /^\d+$/.test(data.formId) ? data.formId : undefined;
  const shouldOverride = Boolean(monarchFormId) && monarchFormId !== cmsFormId;

  const [overrideFormData, setOverrideFormData] = useState<FormData | null>(
    null,
  );
  const [overrideFailed, setOverrideFailed] = useState(false);

  useEffect(() => {
    if (!shouldOverride || !monarchFormId) {
      return;
    }

    let cancelled = false;
    setOverrideFormData(null);
    setOverrideFailed(false);

    fetchTaxiFormPackage(monarchFormId)
      .then((formData) => {
        if (!cancelled) {
          setOverrideFormData(formData);
        }
      })
      .catch((error: Error) => {
        if (cancelled) {
          return;
        }
        window?.newrelic?.noticeError(error, { formId: monarchFormId });
        setOverrideFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldOverride, monarchFormId]);

  if (isLoading) {
    return {
      formId: cmsFormId,
      taxiFormData: cmsTaxiFormData,
      isResolving: true,
    };
  }

  if (!shouldOverride || overrideFailed || !monarchFormId) {
    return {
      formId: cmsFormId,
      taxiFormData: cmsTaxiFormData,
      isResolving: false,
    };
  }

  return {
    formId: monarchFormId,
    taxiFormData: overrideFormData,
    isResolving: !overrideFormData,
  };
}
